import React from "react";

import { PlusOutlined } from "@ant-design/icons";
import { Button, Select, Space } from "antd";
import styled from "styled-components";

import { IGroupByListColumn } from "models";

import type { GeomBinNode } from "../hooks/ring-fence/useRingFenceController";

const { Option, OptGroup } = Select;

interface GeometryBinSelectorProps {
  selectedGeometryBin: string;
  setSelectedGeometryBin: (value: string) => void;
  selectedPolygon: string;
  setSelectedPolygon: (value: string) => void;
  allGeomBins: IGroupByListColumn[];
  geomBinNodes: GeomBinNode[];
  isLoadingGeomBin: boolean;
  onAddRingFence: () => void;
  isAddDisabled: boolean;
  isCreating: boolean;
}

export default function GeometryBinSelector({
  selectedGeometryBin,
  setSelectedGeometryBin,
  selectedPolygon,
  setSelectedPolygon,
  allGeomBins,
  geomBinNodes,
  isLoadingGeomBin,
  onAddRingFence,
  isAddDisabled,
  isCreating
}: GeometryBinSelectorProps) {
  // Group geometry bins by subgroup
  const groupedBins = allGeomBins
    .sort((a: IGroupByListColumn, b: IGroupByListColumn) => {
      // Sort by group first, then by title within each group
      if (a.subgroup !== b.subgroup) {
        return a.subgroup.localeCompare(b.subgroup);
      }
      return a.title.localeCompare(b.title);
    })
    .reduce((groups, bin: IGroupByListColumn) => {
      if (!groups[bin.subgroup]) {
        groups[bin.subgroup] = [];
      }
      groups[bin.subgroup].push(bin);
      return groups;
    }, {} as Record<string, IGroupByListColumn[]>);

  const getPolygonPlaceholder = () => {
    if (!selectedGeometryBin) {
      return "Select a geometry bin first";
    }
    if (isLoadingGeomBin) {
      return "Loading polygons...";
    }
    return "Choose a polygon";
  };

  return (
    <HeaderSection>
      <ControlsRow align="center" wrap>
        <ControlGroup>
          <Label>Geometry Bin:</Label>
          <Select
            value={selectedGeometryBin}
            onChange={setSelectedGeometryBin}
            placeholder="Choose a geometry bin"
            style={{ width: 250 }}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ||
              false
            }>
            {Object.entries(groupedBins)
              .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
              .map(([groupName, bins]) => (
                <OptGroup key={groupName} label={groupName}>
                  {bins.map((bin: IGroupByListColumn) => (
                    <Option key={bin.id} value={bin.id}>
                      {bin.title}
                    </Option>
                  ))}
                </OptGroup>
              ))}
          </Select>
        </ControlGroup>

        <ControlGroup>
          <Label>Polygon:</Label>
          <Select
            value={selectedPolygon}
            onChange={setSelectedPolygon}
            placeholder={getPolygonPlaceholder()}
            style={{ width: 250 }}
            loading={isLoadingGeomBin}
            disabled={!selectedGeometryBin || isLoadingGeomBin}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ||
              false
            }>
            {geomBinNodes
              .filter((node) => !node.isDeleted)
              .map((node: GeomBinNode) => (
                <Option key={node.id} value={node.id}>
                  {node.name || `Polygon ${node.order}`}
                </Option>
              ))}
          </Select>
        </ControlGroup>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onAddRingFence}
          disabled={isAddDisabled}
          loading={isCreating}>
          Add Ring Fence
        </Button>
      </ControlsRow>
    </HeaderSection>
  );
}

const HeaderSection = styled.div`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const ControlsRow = styled(Space)`
  width: 100%;

  .ant-space-item {
    display: flex;
    align-items: center;
  }
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Label = styled.span`
  white-space: nowrap;
  font-weight: 500;
`;
